import React, { useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useLocation, useParams } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import {
  authStateSelector,
  userOrganizationsSelector,
} from '../store/selectors/userSelector'
import { useGetUser } from '../api/auth/useGetUser'
import { setAuthenticated, setUser } from '../store/reducers/userReducer'
import {
  useDealership,
  useDealershipStore,
  usePersistedDealershipStore,
} from '../store/zustand/dealershipStore'
import { find } from 'lodash'
import { GA4 } from '../lib/utils/ga4'

const RequireAuth = ({ children }: { children: JSX.Element }): JSX.Element => {
  const location = useLocation()
  const { agency_id, dealership_id } = useParams()
  const { data: user, isError, isLoading } = useGetUser()
  const dispatch = useDispatch()
  const dealership = useDealership()
  const persistedDealership = usePersistedDealershipStore((state) => state.dealership)
  const setDealership = useDealershipStore((state) => state.setDealership)
  const organizations = useSelector(userOrganizationsSelector)
  const dealerships = useMemo(
    () => organizations?.flatMap((o) => o.dealerships),
    [organizations],
  )
  // This flag is used to indicate that the redux dispatch has been performed.
  const [isLoadingDispatch, setIsLoadingDispatch] = useState(true)
  const isAuthenticated = useSelector(authStateSelector)

  useEffect(() => {
    if (!isLoading && user) {
      GA4.configureUser(user)
    }
  }, [isLoading, user])

  useEffect(() => {
    if (!isLoading) {
      if (!isError) {
        dispatch(setUser(user))
        dispatch(setAuthenticated(true))
        // Slight delay so state updates propagate
        setTimeout(() => setIsLoadingDispatch(false), 0)
      } else {
        setIsLoadingDispatch(false)
      }
    }
  }, [dispatch, isError, isLoading, user])

  useEffect(() => {
    if (isAuthenticated && dealerships && dealerships.length) {
      // Priority: URL > Persisted > First
      if (agency_id && dealership_id) {
        const urlDealership = find(dealerships, {
          id: parseInt(dealership_id),
          organization_id: parseInt(agency_id),
        })
        if (urlDealership) {
          if (dealership) {
            if (
              dealership.id !== urlDealership.id ||
              dealership.organization_id !== urlDealership.organization_id
            ) {
              setDealership(urlDealership)
            }
          } else {
            setDealership(urlDealership)
          }
        }
      } else {
        if (persistedDealership) {
          setDealership(persistedDealership)
        } else {
          setDealership(dealerships[0])
        }
        if (
          persistedDealership &&
          dealership &&
          persistedDealership.id !== dealership.id
        ) {
          setDealership(dealership)
        }
      }
    }
  }, [
    dealership,
    dealerships,
    isAuthenticated,
    persistedDealership,
    setDealership,
    agency_id,
    dealership_id,
  ])

  // Wait for both the API loading and redux dispatch to finish.
  if (isLoading || isLoadingDispatch) {
    return <div>Loading...</div>
  }

  // If not authenticated, redirect to login.
  if (!isAuthenticated) {
    return (
      <Navigate
        to={`/auth/login?next=${location.pathname}`}
        state={{ from: location }}
        replace
      />
    )
  }

  // **Onboarding check**:
  // Once fully loaded, if the user is pending onboarding and is not already on the onboarding route,
  // redirect them to /dashboard/onboarding.
  if (
    user?.pending_onboarding &&
    !location.pathname.startsWith('/dashboard/onboarding')
  ) {
    return <Navigate to="/dashboard/onboarding" replace />
  }

  return children
}

export default RequireAuth
